<app-loading [isLoading]="isLoading()" message="Loading Region Summary" [overlayOpacity]="0.0"></app-loading>
<app-loading [isLoading]="isCreatingNewRoadmap()" message="Creating New Roadmap" [overlayOpacity]="1.0"></app-loading>

@if (!isLoading() && !isCreatingNewRoadmap()) {
  <div class="mx-5">
    <kendo-breadcrumb [items]="breadcrumbs()" (itemClick)="onBreadcrumbClick($event)"></kendo-breadcrumb>
    <br />

    <div class="d-flex flex-row">
      <mat-card class="ra-info mat-elevation-z4 pb-2">
        <mat-card-header>
          <mat-card-title>Region Approver:</mat-card-title>
          <mat-card-subtitle>{{region()?.approverName}} ({{region()?.approverEmail}})</mat-card-subtitle>
        </mat-card-header>
      </mat-card>
    </div>


    <br />
    <mat-tab-group mat-stretch-tabs="false" mat-align-tabs="start">
      <mat-tab>
        <ng-template mat-tab-label>
          <fa-icon class="pe-2" style="font-size:14px" [icon]="faChartBar"></fa-icon>
          Region Summary
        </ng-template>

        <div class="d-flex justify-content-between">
          <div class="me-auto p-2">
            <div class="d-flex flex-row">
              <kendo-chart [style.height.px]="175" [style.width.px]="175" style="margin-top:-20px;">
                <kendo-chart-tooltip>
                  <ng-template
                    kendoChartSeriesTooltipTemplate
                    let-value="value"
                    let-category="category"
                  >
                    {{ category }}: {{ value }}
                  </ng-template>
                </kendo-chart-tooltip>
                <kendo-chart-series>
                  <kendo-chart-series-item
                    type="donut"
                    [data]="chartData()"
                    categoryField="category"
                    field="value"
                    colorField="color"
                  >
                  </kendo-chart-series-item>
                </kendo-chart-series>
                <kendo-chart-legend [visible]="false"></kendo-chart-legend>
              </kendo-chart>
              <div class="p-2">
                <h5>
                  {{tableDisplayTitle()}} <fa-icon style="font-size:14px" [icon]="faQuestionCircle" matTooltip="{{infoLsr}}" matTooltipPosition="below" matTooltipShowDelay="200"></fa-icon>
                </h5>
                @if(showOutsideLSR()) {
                  <p class="mobile-hide-s-2" style="max-width:500px;font-size:11px;">
                    The following includes jobs that are excluded from lean metrics since the job number
                    is not confirmed by the monthly BPC report in SAP <b>or</b> the revenue of the job is
                    below $10,000,000 after it was later synced with BPC.
                  </p>
                }
              </div>
            </div>
          </div>
          <div class="ms-auto p-2">
            <mat-checkbox [ngModel]="showOutsideLSR()" (ngModelChange)="onShowOutsideLSRChange($event)" color="primary">Show jobs outside of LSR</mat-checkbox>
          </div>
        </div>

        <div class="d-flex justify-content-between">
          <div class="me-auto p-2">
            <mat-form-field style="width: 350px;">
              <mat-label>Search Jobs</mat-label>
              <input matInput (keyup)="applyFilter($event.target.value)" placeholder="Type here to search the jobs in the list" style="min-width:50%" />
            </mat-form-field>
          </div>
          <div class="ms-auto p-2">
            <button matButton="filled" (click)="onCreateRoadmapClick()"><b>Create New Roadmap</b></button>
          </div>
        </div>

        @if (!showOutsideLSR() && dataSource().data.length < 1) {
          <div class="summary-internal d-flex justify-content-start">
            <div class="p-2">
              <p>
                No active jobs within Lean Standard Range.
                Click <b class="clicklabel-nojobsinlsr" (click)="onShowOutsideLSRChange(true)" matTooltip="Click to view jobs outside of Lean Standard Range."
                matTooltipPosition="below" matTooltipShowDelay="150">here</b> to show jobs outside of LSR.
              </p>
            </div>
          </div>
        }
        @if(showOutsideLSR() && dataSource().data.length < 1) {
          <div class="summary-internal d-flex justify-content-start">
            <div class="p-2">
              <p>No active jobs found for region. You can be the first to create one!</p>
            </div>
          </div>
        }

        <table mat-table matSort [dataSource]="dataSource()" [hidden]="dataSource().data.length < 1">
          <!-- Project Number-->
          <ng-container matColumnDef="projectNumber">
            <th mat-header-cell mat-sort-header *matHeaderCellDef> Job # </th>
            <td mat-cell *matCellDef="let r">{{r.projectNumber}}</td>
          </ng-container>

          <!-- Project Name -->
          <ng-container matColumnDef="projectName">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Job Name</th>
            <td mat-cell *matCellDef="let r">{{r.projectName}}</td>
          </ng-container>

          <!-- Profit Center Name -->
          <ng-container matColumnDef="profitCenterName">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Profit Center</th>
            <td mat-cell *matCellDef="let r">{{r.profitCenterName}}</td>
          </ng-container>

          <!-- Volume -->
          <ng-container matColumnDef="revenue">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Volume</th>
            <td mat-cell *matCellDef="let r">
              @if(r.isConfidential === 'Yes') {
                CONFIDENTIAL
              } @else {
                ${{r.revenue | number}}
              }
            </td>
          </ng-container>

          <!-- Percent Complete -->
          <ng-container matColumnDef="percentComplete">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>% Complete</th>
            <td mat-cell *matCellDef="let r"> {{r.percentComplete | number: '1.2-2'}}% </td>
          </ng-container>

          <!-- Approved -->
          <ng-container matColumnDef="isApproved">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Approved </th>
            <td mat-cell *matCellDef="let r"> {{r.isApproved ? 'Yes' : 'No'}}</td>
          </ng-container>

          <!-- Lean Champion (renamed to Roadmap Champion)-->
          <ng-container matColumnDef="roadmapChampion">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Roadmap Champion</th>
            <td mat-cell *matCellDef="let r">{{r.roadmapChampion ? r.roadmapChampion : 'Not specified'}}</td>
          </ng-container>

          <!-- Planned Goal Count -->
          <ng-container matColumnDef="plannedCount">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Planned</th>
            <td mat-cell *matCellDef="let r">{{r.plannedCount}}</td>
          </ng-container>

          <!-- Achieved Goal Count -->
          <ng-container matColumnDef="achievedCount">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Achieved </th>
            <td mat-cell *matCellDef="let r"> {{r.achievedCount}}</td>
          </ng-container>

          <!-- Last Edited Date -->
          <ng-container matColumnDef="lastEdited">
            <th class="mobile-col-hide-xs" mat-header-cell mat-sort-header *matHeaderCellDef>Last Edited</th>
            <td class="mobile-col-hide-xs" mat-cell *matCellDef="let r">{{r.lastEdited | date:'MM/dd/yyyy'}}</td>
          </ng-container>

          <!-- Status -->
          <ng-container matColumnDef="statusId">
            <th mat-header-cell mat-sort-header *matHeaderCellDef> Status </th>
            <td mat-cell *matCellDef="let r">
              @if (r.statusId === 1) {
                <fa-icon style="font-size:14px; color:dimgrey" [icon]="faDraftingCompass" matTooltip="Draft, not submitted" matTooltipPosition="left" matTooltipShowDelay="150"></fa-icon>
              } @else if (r.statusId === 2) {
                <fa-icon style="font-size:16px; color:steelblue" [icon]="faAngleDoubleRight" matTooltip="Submitted for approval" matTooltipPosition="left" matTooltipShowDelay="150"></fa-icon>
              } @else if (r.statusId === 3) {
                <fa-icon style="font-size:14px; color:red" [icon]="faUserAltSlash" matTooltip="Rejected, revision needed" matTooltipPosition="left" matTooltipShowDelay="150"></fa-icon>
              } @else if (r.statusId === 4) {
                <fa-icon style="font-size:14px; color:green" [icon]="faUserCheck" matTooltip="Final, approved" matTooltipPosition="left" matTooltipShowDelay="150"></fa-icon>
              } @else if (r.statusId === 5) {
               <fa-icon style="font-size:14px; color:yellowgreen" [icon]="faExclamationCircle" matTooltip="Needs Review" matTooltipPosition="left" matTooltipShowDelay="150"></fa-icon>
              } @else if (r.statusId === 6) {
               <fa-icon style="font-size:16px; color:plum" [icon]="faFileAlt" matTooltip="Dry Run, Approved by an Profit Center Approver, but pending final Regional Approval." matTooltipPosition="right" matTooltipShowDelay="150"></fa-icon>
              }
            </td>
          </ng-container>

          <!-- In LSR -->
          <ng-container matColumnDef="isInLSR">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>LSR</th>
            <td mat-cell *matCellDef="let r">
              @if(r.isInLSR === true) {
                <fa-icon style="font-size:16px;color:green" [icon]="faCheckCircle"
                  matTooltip="In Lean Standard Range" matTooltipPosition="right"
                  matTooltipShowDelay="150"></fa-icon>
              }
              @if(r.isInLSR === false) {
                <fa-icon style="font-size:16px;color:red" [icon]="faTimesCircle"
                  matTooltip="Out of Lean Standard Range" matTooltipPosition="right"
                  matTooltipShowDelay="150"></fa-icon>
              }
            </td>
          </ng-container>

          <!-- Footer-->
          <ng-container matColumnDef="disclaimer">
            <mat-footer-cell *matFooterCellDef colspan="2">
              <b>{{dataSource().data.length}} total roadmap(s) found.</b>
            </mat-footer-cell>
          </ng-container>

          <!-- Definitions -->
          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"> </tr>
          <tr mat-row *matRowDef="let r; columns: displayedColumns" (click)="onRoadmapClick(r.id)"></tr>
          <mat-footer-row *matFooterRowDef="['disclaimer']"></mat-footer-row>
        </table>
      </mat-tab>

      <mat-tab>
        <ng-template mat-tab-label>
          <fa-icon class="pe-2" style="font-size:14px" [icon]="faStream"></fa-icon>
          Last Planner
        </ng-template>

        <div class="summary-internal justify-content-start align-items-center mobile-hide-s">
          <div class="me-auto p-4">
            @if(dataSourceLPS().data.length > 0) {
              <h5>Last Planner - Active Jobs in Lean Standard Range Only</h5>
            }
            @if(dataSourceLPS().data.length < 1) {
              <h5>No Last Planner Jobs for this Region</h5>
              <p>
                There is no Last Planner data found for this region. Last Planner jobs show up on this page when they are within
                <b matTooltip="{{lsrTooltip}}" matTooltipPosition="below" matTooltipShowDelay="200">Lean Standard Range <fa-icon style="font-size:12px" [icon]="faQuestionCircle"></fa-icon></b>,
                <b>active</b>, and have at least <b>1 Last Planer goal</b> specified on the roadmap.
              </p>
            }
          </div>
        </div>

        <div class="d-flex justify-content-between">
          <div class="me-auto p-2" [hidden]="dataSourceLPS().data.length < 1">
            <mat-form-field style="width: 350px;">
              <mat-label>Search Jobs</mat-label>
              <input matInput (keyup)="applyFilterLPS($event.target.value)" placeholder="Type here to search last planner jobs in the list" style="min-width:50%" />
            </mat-form-field>
          </div>
          <div class="ms-auto p-2">
            <button matButton="filled" (click)="onCreateRoadmapClick()"><b>Create New Roadmap</b></button>
          </div>
        </div>

        <table mat-table matSort [dataSource]="dataSourceLPS()" [hidden]="dataSourceLPS().data.length < 1">
          <!-- Project Number-->
          <ng-container matColumnDef="projectNumber">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Job #</th>
            <td mat-cell *matCellDef="let r">{{r.projectNumber}}</td>
          </ng-container>

          <!-- Project Name -->
          <ng-container matColumnDef="projectName">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Job Name</th>
            <td mat-cell *matCellDef="let r">{{r.projectName}}</td>
          </ng-container>

          <!-- Percent Complete -->
          <ng-container matColumnDef="percentComplete">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>% Complete</th>
            <td mat-cell *matCellDef="let r">{{r.percentComplete | number: '1.2-2'}}%</td>
          </ng-container>

          <!-- For Each LPS Goal -->
          @for (lpsGoal of lastPlannerGoals(); track lpsGoal.id; let i = $index) {
            <ng-container [matColumnDef]="lpsGoal.id!.toString()">
              <th mat-header-cell *matHeaderCellDef>{{lpsGoal.goalShortName}}</th>
              <td mat-cell *matCellDef="let r;"
                [class.goal-notplanned]="r.lastPlannerConditions[i].currentConditionId == ''"
                [class.goal-achieved]="r.lastPlannerConditions[i].currentConditionId == 1"
                [class.goal-na]="r.lastPlannerConditions[i].currentConditionId == 2"
                [class.goal-notachieved]="r.lastPlannerConditions[i].currentConditionId == 3"
                [class.goal-isplanned]="r.lastPlannerConditions[i].currentConditionId == 4">
                @switch (r.lastPlannerConditions[i].currentConditionId) {
                  @case (0) {
                    Not Planned
                  }
                  @case (4) {
                    Planned Goal
                  }
                  @case (1) {
                    Achieved
                  }
                  @case (2) {
                    In Progress
                  }
                  @case (3) {
                    Not Achieved
                  }
                }
              </td>
            </ng-container>
          }
          <!-- Footer -->
          <ng-container matColumnDef="disclaimer">
            <mat-footer-cell *matFooterCellDef colspan="2">
              <b>{{dataSourceLPS().data.length}} total roadmap(s) found.</b>
            </mat-footer-cell>
          </ng-container>

          <!-- Definitions -->
          <tr mat-header-row *matHeaderRowDef="displayedColumnsLPS(); sticky: true"></tr>
          <tr mat-row *matRowDef="let r; columns: displayedColumnsLPS()" (click)="onRoadmapClick(r.id)"></tr>
          <tr mat-footer-row *matFooterRowDef="['disclaimer']"></tr>
        </table>
      </mat-tab>
    </mat-tab-group>
  </div>
}
